import { PageType, DeviceFilter, PageExistence } from '../types';
import { LOCK_ICON_SVG, PAGE_NAMES, DEVICE_FILTER_NAMES } from '../constants';
import { SEMANTIC_COLORS } from '../design-system/colors';

/**
 * Manages page type and device filter selection
 */
export class PageManager {
  private currentPageType: PageType = 'product';
  private currentDeviceFilter: DeviceFilter = 'all';
  private pageExists: PageExistence = {
    product: true,
    collection: false,
    home: false,
    cart: false
  };

  private onPageChangeCallback: ((pageType: PageType) => void) | null = null;
  private onDeviceChangeCallback: ((filter: DeviceFilter) => void) | null = null;

  /**
   * Initialize page manager
   * @param pageExists - Object indicating which pages exist
   */
  initialize(pageExists: PageExistence): void {
    this.pageExists = pageExists;
    this.setupPageTypeListeners();
    this.setupDeviceFilterListener();
    this.updatePageTypeTabs();
  }

  /**
   * Set up page type button listeners
   */
  private setupPageTypeListeners(): void {
    document.querySelectorAll('.page-type-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const button = btn as HTMLButtonElement;
        const pageType = button.dataset.pageType as PageType;

        const inactiveClasses = SEMANTIC_COLORS.pageType.inactive.split(' ');

        document.querySelectorAll('.page-type-btn').forEach((b) => {
          b.classList.remove(...SEMANTIC_COLORS.pageType.active.split(' '));
          b.classList.add(...inactiveClasses);
        });
        button.classList.remove(...inactiveClasses);
        button.classList.add(...SEMANTIC_COLORS.pageType.active.split(' '));

        // Update state
        this.currentPageType = pageType;
        this.updatePageContext();

        // Trigger callback
        if (this.onPageChangeCallback) {
          this.onPageChangeCallback(pageType);
        }
      });
    });
  }

  /**
   * Set up device filter dropdown listener
   */
  private setupDeviceFilterListener(): void {
    const deviceFilter = document.getElementById('device-filter') as HTMLSelectElement;
    if (!deviceFilter) return;

    deviceFilter.addEventListener('change', (e) => {
      const select = e.target as HTMLSelectElement;
      this.currentDeviceFilter = select.value as DeviceFilter;
      this.updatePageContext();

      // Trigger callback
      if (this.onDeviceChangeCallback) {
        this.onDeviceChangeCallback(this.currentDeviceFilter);
      }
    });
  }

  /**
   * Update page type tabs with lock icons
   */
  private updatePageTypeTabs(): void {
    const pageTypeButtons = document.querySelectorAll('.page-type-btn');

    pageTypeButtons.forEach((btn) => {
      const button = btn as HTMLButtonElement;
      const pageType = button.dataset.pageType as PageType;
      const pageExist = this.pageExists[pageType];

      const pageTypeNames: Record<PageType, string> = {
        home: 'Home',
        collection: 'Collection',
        product: 'Product',
        cart: 'Cart'
      };

      if (!pageExist) {
        // Add lock icon (still clickable to show unavailable page message)
        button.innerHTML = `${pageTypeNames[pageType]} ${LOCK_ICON_SVG}`;
        button.classList.add('opacity-60');
      } else {
        // Normal display
        button.innerHTML = `${pageTypeNames[pageType]}`;
        button.classList.remove('opacity-60');
      }
    });
  }

  /**
   * Update page context display
   */
  private updatePageContext(): void {
    const pageContext = document.getElementById('page-context');
    if (!pageContext) return;

    const pageName = PAGE_NAMES[this.currentPageType] || 'Page';
    const filterName = DEVICE_FILTER_NAMES[this.currentDeviceFilter] || 'All Devices';

    pageContext.textContent = `${pageName} - ${filterName}`;
  }

  /**
   * Register callback for page type changes
   */
  onPageChange(callback: (pageType: PageType) => void): void {
    this.onPageChangeCallback = callback;
  }

  /**
   * Register callback for device filter changes
   */
  onDeviceFilterChange(callback: (filter: DeviceFilter) => void): void {
    this.onDeviceChangeCallback = callback;
  }

  /**
   * Get current page type
   */
  getCurrentPageType(): PageType {
    return this.currentPageType;
  }
}
